import type { StoryboardCue } from '../model/media.ts';
import type { Chapter } from '../model/playback.ts';

/**
 * The arithmetic behind §7's chrome: the segmented progress bar and the storyboard scrub preview.
 *
 * It lives in core rather than in the screen for the same reason `parseChapters` does. A chaptered
 * bar and a cropped preview tile are both *geometry over domain data*, Compose has to draw exactly
 * the same two things on TV, and a version of this that lives in a `.tsx` is a version that is
 * untested and re-derived from scratch on Android. What stays on the platform side is the part that
 * is genuinely CSS: which property carries `scale`, and what a flex weight is called.
 *
 * Nothing here reads a clock, touches a DOM node or formats a string (R1, R7).
 */

// ---------------------------------------------------------------------------
// The progress bar
// ---------------------------------------------------------------------------

/**
 * One cell of §7's "flex row of segments, `flex-grow: duration`, each filled from `currentTime`".
 *
 * `playedFraction`/`bufferedFraction` are **local** to the segment (0 at its start, 1 at its end)
 * rather than global, because that is what a nested fill element needs — a renderer given global
 * fractions has to redo this subtraction per segment and will get the clamp wrong somewhere.
 */
export interface TimelineSegment {
  /** Index into the chapter list. `0` on the single segment of an unchaptered bar. */
  readonly index: number;
  /** `null` when the video has no chapters — the bar is one plain segment, not a fake chapter. */
  readonly title: string | null;
  readonly startSec: number;
  readonly endSec: number;
  /** `endSec - startSec`, the flex weight. Never 0, so a zero-width cell cannot swallow its gap. */
  readonly durationSec: number;
  readonly playedFraction: number;
  readonly bufferedFraction: number;
  /** The playhead is inside this segment. Exactly one segment has this once playback has a position. */
  readonly active: boolean;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function localFraction(position: number, startSec: number, endSec: number): number {
  const span = endSec - startSec;
  if (span <= 0) return position >= endSec ? 1 : 0;
  return clamp01((position - startSec) / span);
}

/**
 * The bar, as data. Always at least one segment: an unchaptered video is one cell spanning the whole
 * timeline, so the renderer has a single code path rather than a chaptered branch and a plain one.
 *
 * The **duration is the authority**, not the chapter list. `parseChapters` derives its last `endSec`
 * from `lengthSeconds`, which the engine's own `duration` disagrees with by a frame or two often
 * enough to matter; a bar built from the chapters alone then ends slightly before or after the
 * scrub input's range and the playhead never reaches the end of the last cell. So chapters are
 * clamped into `[0, durationSec]`, any that start past the end are dropped, and the last surviving
 * one is stretched to meet it.
 */
export function timelineSegments(
  chapters: readonly Chapter[],
  durationSec: number,
  positionSec: number,
  bufferedSec: number,
): readonly TimelineSegment[] {
  const total = Number.isFinite(durationSec) && durationSec > 0 ? durationSec : 0;
  const position = Number.isFinite(positionSec) ? Math.max(0, positionSec) : 0;
  const buffered = Number.isFinite(bufferedSec) ? Math.max(0, bufferedSec) : 0;

  const usable = chapters.filter((chapter) => total === 0 || chapter.startSec < total);
  if (usable.length === 0) {
    return [
      {
        index: 0,
        title: null,
        startSec: 0,
        endSec: total,
        durationSec: total,
        playedFraction: localFraction(position, 0, total),
        bufferedFraction: localFraction(buffered, 0, total),
        active: true,
      },
    ];
  }

  const activeIndex = chapterIndexAt(usable, position);

  return usable.map((chapter, index): TimelineSegment => {
    const startSec = Math.max(0, Math.min(chapter.startSec, total));
    const isLast = index === usable.length - 1;
    // Not `chapter.endSec`: consecutive chapters must share a boundary exactly, or the bar shows a
    // hairline of unfilled track between two cells that are both fully played.
    const nextStart = usable[index + 1]?.startSec ?? total;
    const endSec = isLast ? total : Math.max(startSec, Math.min(nextStart, total));

    return {
      index,
      title: chapter.title,
      startSec,
      endSec,
      durationSec: Math.max(0, endSec - startSec),
      playedFraction: localFraction(position, startSec, endSec),
      bufferedFraction: localFraction(buffered, startSec, endSec),
      active: index === activeIndex,
    };
  });
}

/** Which chapter the playhead is inside. `null` before the first one, which `0:00` makes rare. */
export function chapterIndexAt(chapters: readonly Chapter[], positionSec: number): number | null {
  for (let index = chapters.length - 1; index >= 0; index -= 1) {
    const chapter = chapters[index];
    if (chapter !== undefined && positionSec >= chapter.startSec) return index;
  }
  return null;
}

/** Pointer position along the bar → a time. Total 0 (live, or duration not known yet) answers 0. */
export function seekSecondsAt(fraction: number, durationSec: number): number {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return 0;
  return clamp01(fraction) * durationSec;
}

/** The inverse, for painting. Kept next to its partner so the two clamps cannot drift apart. */
export function positionFraction(positionSec: number, durationSec: number): number {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return 0;
  return clamp01(positionSec / durationSec);
}

// ---------------------------------------------------------------------------
// The scrub preview
// ---------------------------------------------------------------------------

/**
 * A storyboard tile, ready to draw, in units a renderer can use unchanged.
 *
 * The crop is expressed as **offset plus scale** rather than as a `background-size`, because the
 * sheet's own dimensions are nowhere in the data: the VTT gives a tile rectangle and a sheet URL and
 * never says how big the sheet is. Offsetting by the tile's own `x`/`y` at natural size and scaling
 * the result needs no such number — and it is the same pair of values Compose's `drawImage` takes.
 */
export interface StoryboardPreview {
  readonly imageUrl: string;
  /** Natural tile size, i.e. the crop window before scaling. */
  readonly tileWidth: number;
  readonly tileHeight: number;
  /** Where the tile sits inside the sheet. A renderer shifts the sheet by *minus* these. */
  readonly offsetX: number;
  readonly offsetY: number;
  readonly scale: number;
  /** Tile size after scaling — what the visible box measures. */
  readonly boxWidth: number;
  readonly boxHeight: number;
}

/**
 * `null` rather than a degenerate box when there is no cue, or when the cue carries no tile size.
 *
 * A zero-sized tile is a real state, not a defensive branch: `parseStoryboardVtt` falls back to the
 * spec's `width`/`height` when a cue URL has no `#xywh=` fragment, and the spec's own numbers are
 * `null` on a live video. Returning a 0×0 preview would render as an invisible box that looks like
 * a broken image rather than like the absent feature it is.
 */
export function storyboardPreview(
  cue: StoryboardCue | null,
  targetWidthPx: number,
): StoryboardPreview | null {
  if (cue === null || cue.w <= 0 || cue.h <= 0) return null;

  const target = Number.isFinite(targetWidthPx) && targetWidthPx > 0 ? targetWidthPx : cue.w;
  const scale = target / cue.w;

  return {
    imageUrl: cue.imageUrl,
    tileWidth: cue.w,
    tileHeight: cue.h,
    offsetX: cue.x,
    offsetY: cue.y,
    scale,
    boxWidth: Math.round(cue.w * scale),
    boxHeight: Math.round(cue.h * scale),
  };
}

/**
 * {@link storyboardPreview} for a box whose *both* sides are fixed — the grid's hover preview,
 * where the thumbnail is 16:9 whatever the video is. Contain-fit, never cover (2026-08-02):
 * a vertical Short's tiles arrive 99×180 from the same `width=320&height=180` request
 * (measured — the parameters pick a level, they do not letterbox it), and scaling those to the
 * box's width shows the top slice of the picture and nothing else. The scaled tile is at most the
 * box on both axes; the renderer centres it and lets the box's own black be the letterbox.
 */
export function storyboardPreviewContain(
  cue: StoryboardCue | null,
  boxWidthPx: number,
  boxHeightPx: number,
): StoryboardPreview | null {
  if (cue === null || cue.w <= 0 || cue.h <= 0) return null;
  if (!Number.isFinite(boxWidthPx) || boxWidthPx <= 0) return null;
  if (!Number.isFinite(boxHeightPx) || boxHeightPx <= 0) return null;

  const scale = Math.min(boxWidthPx / cue.w, boxHeightPx / cue.h);

  return {
    imageUrl: cue.imageUrl,
    tileWidth: cue.w,
    tileHeight: cue.h,
    offsetX: cue.x,
    offsetY: cue.y,
    scale,
    boxWidth: Math.round(cue.w * scale),
    boxHeight: Math.round(cue.h * scale),
  };
}

// ---------------------------------------------------------------------------
// Auto-hiding the chrome
// ---------------------------------------------------------------------------

/** How long the pointer may sit still over a playing video before the chrome fades out. */
export const CONTROLS_IDLE_MS = 2600;

export interface ChromeVisibility {
  readonly playing: boolean;
  readonly pointerIdleMs: number;
  /** Focus is somewhere in the chrome — hiding it would hide the focus ring (§9). */
  readonly focusInside: boolean;
  /**
   * The pointer is over the **bar**, not over the picture.
   *
   * Its own input rather than a longer timeout, because `pointerIdleMs` is measuring the wrong thing
   * there: a pointer that stops over the picture has stopped watching the controls, while a pointer
   * that stops over the controls has *arrived* — it is aiming at one. Always `false` on a platform
   * with no pointer, which is what leaves the TV with the timer and `focusInside`.
   */
  readonly pointerOverChrome: boolean;
  readonly menuOpen: boolean;
  readonly scrubbing: boolean;
}

/**
 * Hiding the chrome is not just cosmetic: it overlays the picture, so leaving it up covers the
 * bottom of every video. Every clause below is a case where hiding it would take a control away from
 * someone who is in the middle of using it — which is why this is a function rather than a timer
 * with an `if` in it.
 *
 * The cost of getting it wrong is more than a bar that vanishes: a hidden bar is not hit-testable
 * (`inert`), so the click that was aimed at it lands on the video instead, and a click on the video
 * is a play/pause toggle (`decideTap`).
 */
export function chromeVisible(state: ChromeVisibility): boolean {
  if (!state.playing) return true;
  if (state.menuOpen || state.scrubbing || state.focusInside) return true;
  if (state.pointerOverChrome) return true;
  return state.pointerIdleMs < CONTROLS_IDLE_MS;
}

// ---------------------------------------------------------------------------
// The native range under the painted bar
// ---------------------------------------------------------------------------

/**
 * How finely the scrub bar's `input[type=range]` is addressed: tenths of a second.
 *
 * Steps per second rather than the step itself, because the quantiser has to divide rather than
 * multiply — `Math.round(seconds / 0.1) * 0.1` gives `160.70000000000002` for `160.7`, which is a
 * number the element cannot hold and therefore the whole bug this exists to prevent.
 */
const SCRUB_STEPS_PER_SEC = 10;

export const SCRUB_STEP_SEC = 1 / SCRUB_STEPS_PER_SEC;

/** Round a position the way the element itself will, so handing it back is a fixed point. */
function toScrubStep(seconds: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return Math.round(seconds * SCRUB_STEPS_PER_SEC) / SCRUB_STEPS_PER_SEC;
}

/** The four numbers a range element needs to describe a playhead. */
export interface ScrubRange {
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly value: number;
}

/**
 * What to hand the native range, quantised to its own step — and that is the whole point of the
 * function rather than a detail of it.
 *
 * A range element **snaps** its value to the step, so a position like `38.98223` is stored as `39`.
 * A framework that controls the element compares what it last wrote with what the element now holds
 * to decide whether the *user* changed it, so a value it can never store verbatim leaves the two
 * permanently disagreeing: the element's own snapped number is then reported as an edit, at whatever
 * moment the next event arrives, and the seek that lands is to a position nobody asked for. Measured
 * on this app's Watch page as a seek that visibly took and was then undone ~8 ms later, from a
 * `change` event carrying the pre-drag playhead — three or four attempts to move the playhead before
 * one stuck. `max` is quantised for the same reason: a value inside the last step of an unquantised
 * maximum snaps *past* it and is then clamped back.
 *
 * `max` is the largest of three numbers rather than the duration, which is what keeps `value <= max`
 * true by construction instead of by a clamp. One step, because `max: 0` is a thumb that cannot move
 * at all and that is the state every video is in for its first few hundred milliseconds; and the
 * value itself, so a duration that is unknown or infinite parks the playhead where it actually is
 * rather than pinning it to the end of a one-step bar.
 */
export function scrubRange(displayedSec: number, durationSec: number): ScrubRange {
  const value = toScrubStep(displayedSec);
  return {
    min: 0,
    max: Math.max(SCRUB_STEP_SEC, value, toScrubStep(durationSec)),
    step: SCRUB_STEP_SEC,
    value,
  };
}

// ---------------------------------------------------------------------------
// The shape of the stage
// ---------------------------------------------------------------------------

/**
 * The shape every box that stands in for the picture draws, and the floor the stage never goes
 * under: a video narrower than this pillarboxes inside it rather than shrinking the box.
 */
export const STAGE_BASE_ASPECT = 16 / 9;

/** The cinema shape the widen control asks for, cropped out of a 16∶9 frame. */
export const STAGE_CROP_ASPECT = 21 / 9;

/**
 * How far a measured ratio may sit from 16∶9 and still be read as 16∶9. A 1920×1088 encode — the
 * padded height a 16-pixel macroblock grid produces — measures 1.7647, which is 0.013 off and is the
 * number this tolerance exists for. It stays well clear of the shapes on either side: 1.85∶1 is 0.072
 * above, and 5∶3 is 0.111 below.
 */
const STAGE_ASPECT_TOLERANCE = 0.02;

/** What the stage is being asked to hold. Dimensions are the loaded element's own. */
export interface StageShapeInput {
  /** `0` while nothing has been measured, which is the state every video starts in. */
  readonly videoWidth: number;
  readonly videoHeight: number;
  /**
   * Whether the page has width to spend on a picture that is wider than 16∶9. Only theater does:
   * everywhere else the column's width is the page's, so the box could only get *shorter*, which is
   * the page's own layout to keep.
   */
  readonly theater: boolean;
  /** The picture is the whole screen, which is the other box a crop can fill. */
  readonly fullscreen: boolean;
  /** The reader pressed the widen control. Ignored unless {@link StageShape.cropAvailable}. */
  readonly cropRequested: boolean;
}

/** The three things a renderer needs to draw the stage: its ratio, its fit, and its control. */
export interface StageShape {
  /**
   * The ratio the stage box takes, width over height, never below {@link STAGE_BASE_ASPECT}. The
   * renderer spends it twice — as the box's own ratio, and as the multiplier that turns the height
   * cap into a width, which is what makes a wider picture wider instead of shorter.
   */
  readonly aspect: number;
  /** Scale the picture to fill that box, cutting off what falls outside it. */
  readonly crop: boolean;
  /**
   * Whether the widen control has anything to offer this video: a 16∶9 picture, in theater or in
   * fullscreen. On the ordinary page the answer is no however the picture is shaped.
   */
  readonly cropAvailable: boolean;
}

/**
 * What shape to give the stage for a picture of this size.
 *
 * Two decisions in one function, because they are the same decision seen from two sides. A video
 * **wider** than 16∶9 gets a box of its own shape, so the letterbox bars the fixed 16∶9 frame drew
 * above and below it are gone. A video **at** 16∶9 gets the offer instead: cinema uploads carry
 * their bars inside the frame, where no ratio can see them, so the only way out is the reader saying
 * the picture is wider than the frame it arrived in — which is what {@link cropAvailable} is, and
 * why the offer is absent on any video whose shape already answers the question.
 *
 * The two are gated differently, and that is the one thing to hold on to here. Reshaping the box
 * around a wide picture needs somewhere to put the extra width, so it happens in theater alone. The
 * crop needs a box whose **height** is free to move, which theater and fullscreen both have and the
 * ordinary page does not: there the column's width is fixed, so filling a 21∶9 box would take the
 * bars away by making the video shorter, and a zoom that shrinks the picture is not a zoom. So the
 * page is offered nothing, and a crop asked for in one of the other two modes is inert while the
 * reader is back on it — not forgotten, since returning to either mode is what it was asked for.
 */
export function stageShape(input: StageShapeInput): StageShape {
  const measured =
    Number.isFinite(input.videoWidth) &&
    Number.isFinite(input.videoHeight) &&
    input.videoWidth > 0 &&
    input.videoHeight > 0
      ? input.videoWidth / input.videoHeight
      : null;

  if (measured === null) {
    return { aspect: STAGE_BASE_ASPECT, crop: false, cropAvailable: false };
  }

  const cropAvailable =
    (input.theater || input.fullscreen) &&
    Math.abs(measured - STAGE_BASE_ASPECT) <= STAGE_ASPECT_TOLERANCE;
  if (cropAvailable && input.cropRequested) {
    return { aspect: STAGE_CROP_ASPECT, crop: true, cropAvailable: true };
  }
  if (!input.theater) return { aspect: STAGE_BASE_ASPECT, crop: false, cropAvailable };
  return { aspect: Math.max(measured, STAGE_BASE_ASPECT), crop: false, cropAvailable };
}
